<script setup lang="tsx">
import type { Characteristic } from '~/components/CharacteristicsGrid';
import type { SharingOption } from '~/components/ShareContent';
import type { Tab } from '~/components/TabsNavigator';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { Agent } from '~/core/entities/agent/Agent';
import type { PropertyTypeObject } from '~/core/entities/CRMEnums';
import type { Property } from '~/core/entities/property';
import type { SectionName } from '~/types/mixpanel';

import { onClickOutside, useClipboard } from '@vueuse/core';
import { floatToLocaleString } from '#imports';
import { useEvents } from '~/composables/events/useEvents';
import { usePropertyPageJsonLd } from '~/composables/jsonLd/usePropertyPageJsonLd';
import { useSeoTagDetails } from '~/composables/useSeoTagsDetails';
import { PropertyPublicationStatus } from '~/core/entities/api/ApiProperty';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { PropertyPresenter } from '~/presenters/PropertyPresenter';
import { useNavigationStore } from '~/store/navigationStore';
import { concatUrlPath, getPropertyImageAlt } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';
import { getAgentFirstName, grammarInflection } from '~~/utils';
import { useRoute } from 'vue-router';

import CharacteristicsGrid from '~/components/CharacteristicsGrid/CharacteristicsGrid.vue';
import { CopyButton } from '~/components/CopyButton';
import { CopyToastDescription } from '~/components/CopyToastDescription';
import { FeaturesGrid } from '~/components/FeaturesGrid';
import { PropertyPrices } from '~/components/Property/PropertyPrices';
import { ChosenPropertiesSection } from '~/components/Sections/ChosenPropertiesSection';
import { HighlightedRegionPropertiesDetailsSection } from '~/components/Sections/HighlightedRegionPropertiesDetailsSection';
import { PropertyContactSection } from '~/components/Sections/PropertyContactSection';
import { PropertyDescriptionSection } from '~/components/Sections/PropertyDescriptionSection';
import { PropertyHighlightedImagesSection } from '~/components/Sections/PropertyHighlightedImagesSection';
import { PropertyLocationSection } from '~/components/Sections/PropertyLocationSection';
import { ShareContent } from '~/components/ShareContent';
import { Breadcrumb } from '~/components/ui/breadcrumb';
import { Skeleton } from '~/components/ui/skeleton';
import { useToast } from '~/components/ui/toast';

import IconMaterialSymbolsLightBathtubOutline from '~icons/material-symbols-light/bathtub-outline';
import IconMaterialSymbolsLightBedOutlineSharp from '~icons/material-symbols-light/bed-outline-sharp';
import IconMaterialSymbolsLightDirectionsCarOutline from '~icons/material-symbols-light/directions-car-outline';
import IconMaterialSymbolsLightOpenInFull from '~icons/material-symbols-light/open-in-full';
import IconMaterialSymbolsShare from '~icons/material-symbols-light/share-outline';
import IconMaterialSymbolsLightShowerOutline from '~icons/material-symbols-light/shower-outline';
import IconMaterialSymbolsStraighten from '~icons/material-symbols-light/straighten-outline';

definePageMeta({
	layout: 'property-details'
});

const {
	trackSharePropertyClicked,
	trackCtaViewMoreClicked,
	trackBreadcrumbClicked,
	trackCtaOffMarketClicked,
	trackModalPhotoOpened,
	trackModalVideoOpened,
	trackMapPropertyOpened,
	trackFormPropertyClicked
} = useEvents();
const { setPreferences } = usePreferencesCookie();

const { ORIGIN } = useRuntimeConfig().public;
const { copy } = useClipboard();
const { toast } = useToast();
const navigationStore = useNavigationStore();

const isRunningOnClient = import.meta.client;

const router = useRouter();
const { commercialId: routeCommercialId, slug } = useRoute().params as {
	commercialId: string;
	slug: string;
};

useHead({
	link: [
		{
			rel: 'canonical',
			href: `${ORIGIN}/imovel/${routeCommercialId}/${slug}`
		}
	],
	meta: [
		{
			property: 'og:url',
			content: `${ORIGIN}/imovel/${routeCommercialId}/${slug}`
		}
	]
});

onMounted(() => {
	setPreferences({
		regions: property.value?.region ? [property.value?.region] : [],
		askingPrice: property.value?.askingPrice ?? 0
	});
});

const { data: property, status: propertyStatus } =
	await useAsyncData<Property | null>(
		`imovel:${routeCommercialId}`,
		async () => {
			const [property, error] =
				await nuxtPropertiesRepository.getOne(routeCommercialId);

			if (!property || error) {
				navigateTo('/404');
			}

			if (property?.publicationStatus !== PropertyPublicationStatus.PUBLISHED) {
				navigateTo({
					path: `/imovel-indisponivel/${routeCommercialId}`
				});
			}

			return property;
		},
		{
			lazy: true,
			default: () => null,
			...getCacheAsyncDataConfig<Property | null>(30 * 60 * 1000)
		}
	);

const isFetchingProperty = computed<boolean>(
	() => propertyStatus.value === 'pending' && isRunningOnClient
);

useSeoTagDetails(property);

const { data: similarProperties, status: similarPropertiesStatus } =
	useAsyncData<Property[]>(
		`imovel:${routeCommercialId}:similar-properties`,
		async () => {
			await until(property).toBeTruthy();

			if (!property.value) return [];

			const [similarProperties, error] =
				await nuxtPropertiesRepository.getSimilarProperties({
					identifier: routeCommercialId,
					propertyType: [property.value?.propertyType.identifier],
					region: property.value?.region ?? undefined
				});

			if (!similarProperties || error) return [];

			return similarProperties || [];
		},
		{
			lazy: true
		}
	);

const isFetchingSimilarProperties = computed<boolean>(
	() => similarPropertiesStatus.value === 'pending' && isRunningOnClient
);

const { data: chosenForYouProperties, status: chosenForYouPropertiesStatus } =
	useAsyncData<Property[] | null>(
		`imovel:${routeCommercialId}:chosen-for-you`,
		async () => {
			await until(property).toBeTruthy();

			if (!property.value) return null;

			const [chosenForYouProperties, error] =
				await nuxtPropertiesRepository.getChosenForYouProperties({
					identifier: routeCommercialId,
					regions: [property.value.region] as string[],
					askingPrice: property.value.askingPrice ?? undefined
				});

			if (!chosenForYouProperties || error) return null;

			return chosenForYouProperties;
		},
		{
			lazy: true
		}
	);

const isFetchingChosenForYouProperties = computed<boolean>(
	() => chosenForYouPropertiesStatus.value === 'pending' && isRunningOnClient
);

whenever(
	property,
	async ({ commercialId, slug }) => {
		const canonicalPath = `/imovel/${commercialId}/${slug}`;
		const isOnCanonicalPath = router.currentRoute.value.path === canonicalPath;

		if (!isOnCanonicalPath) await router.replace(canonicalPath);
	},
	{ immediate: true }
);

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
	if (!property.value) return [];

	return PropertyPresenter.getBreadcrumItems(property.value);
});

const characteristicsKeys = readonly([
	'area',
	'totalArea',
	'bedrooms',
	'suites',
	'bathrooms',
	'parkingSpots'
] as const);

const characteristicHandler = computed<
	Record<
		(typeof characteristicsKeys)[number],
		(value: number) => Characteristic
	>
>(() => ({
	area: (value: number) => {
		const squareMeterPrice = (property.value?.askingPrice || 0) / value;

		const squareMeterPriceCurrency = floatToLocaleString(squareMeterPrice, {
			style: 'currency'
		});

		function DescriptionTextVnode(): VNode {
			return (
				<>
					Área útil{' '}
					<span class="hidden md:inline-block">
						({squareMeterPriceCurrency}/m²)
					</span>
				</>
			);
		}

		return {
			label: `${value} m²`,
			description: DescriptionTextVnode(),
			icon: IconMaterialSymbolsStraighten
		};
	},
	totalArea: (value: number) => ({
		label: `${value} m²`,
		description: 'Área total',
		icon: IconMaterialSymbolsLightOpenInFull
	}),
	bedrooms: (value: number) => ({
		label: `${value} ${grammarInflection(value, 'quarto', 'quartos')}`,
		description: '',
		icon: IconMaterialSymbolsLightBedOutlineSharp
	}),
	bathrooms: (value: number) => ({
		label: `${value} ${grammarInflection(value, 'banheiro', 'banheiros')}`,
		description: '',
		icon: IconMaterialSymbolsLightShowerOutline
	}),
	suites: (value: number) => ({
		label: `${value} ${grammarInflection(value, 'suíte', 'suítes')}`,
		description: '',
		icon: IconMaterialSymbolsLightBathtubOutline
	}),
	parkingSpots: (value: number) => ({
		label: `${value} ${grammarInflection(value, 'vaga', 'vagas')}`,
		description: '',
		icon: IconMaterialSymbolsLightDirectionsCarOutline
	})
}));

const characteristics = computed<Characteristic[]>(
	() =>
		characteristicsKeys
			.map(key => {
				const value = property.value?.[key] ?? 0;
				if (value <= 0) return null;

				return characteristicHandler.value[key](value);
			})
			.filter(Boolean) as Characteristic[]
);

const hasFeaturesByCategory = computed<boolean>(
	() => !isEmpty(property.value?.featuresByCategory)
);

const copyCommercialId = async () => {
	if (import.meta.client) await copy(property.value?.commercialId ?? '');
};

const hasImagesEnoughToShowCharacteristicsAndHighlightedImagesSections =
	computed<boolean>(
		() =>
			!isEmpty(property.value?.images) &&
			(property.value?.images.length ?? 0) >= 8
	);

const shouldShowCharacteristicsAndHighlightedImagesSection = computed<boolean>(
	() =>
		hasFeaturesByCategory.value ||
		hasImagesEnoughToShowCharacteristicsAndHighlightedImagesSections.value ||
		isFetchingProperty.value
);

const shouldShowFeaturesSection = computed<boolean>(
	() => hasFeaturesByCategory.value || isFetchingProperty.value
);

const shouldShowHighlightedImagesSection = computed<boolean>(
	() =>
		hasImagesEnoughToShowCharacteristicsAndHighlightedImagesSections.value ||
		isFetchingProperty.value
);

const launchYear = computed<string>(() => {
	const date = property.value?.launchDate;

	if (!date) return '';

	return new Date(date).getFullYear().toString();
});

const numberOfImages = computed<number>(
	() => property.value?.images.length ?? 0
);
const firstImage = computed<string | null>(
	() => property.value?.images[0]?.watermarkUrl ?? null
);
const hasSecondAndThirdImages = computed<boolean>(() =>
	isFetchingProperty.value
		? true
		: !!(
				property.value?.images[1]?.watermarkUrl &&
				property.value?.images[2]?.watermarkUrl
			)
);
const numberOfVideos = computed<number>(
	() => property.value?.videos.length ?? 0
);
const agentFirstName = computed<string>(() =>
	getAgentFirstName(property.value?.agent)
);
const heroCtaText = computed(() => `Falar com ${agentFirstName.value}`);
const shareUrl = computed<string>(() =>
	concatUrlPath(ORIGIN, router.currentRoute.value.fullPath)
);

const similarPropertiesHref = computed<string | null>(() => {
	if (!property.value) return null;

	return PropertyFiltersPresenter.getPathFromFilters({
		cities: [property.value.city],
		states: [property.value.state],
		regions: [property.value.region],
		propertyTypes: [property.value.propertyType.identifier],
		maxAskingPrice: property.value.askingPrice || undefined
	});
});

const chosenForYouCtaLink = computed<string>(() =>
	PropertyFiltersPresenter.getPathFromFilters({
		regions: [property.value?.region ?? ''],
		states: [property.value?.state ?? ''],
		cities: [property.value?.city ?? ''],
		maxAskingPrice: property.value?.askingPrice
			? Math.ceil(property.value.askingPrice * 1.15)
			: undefined,
		isChosenForYou: true
	})
);

const showShareContent = ref(false);
const isPropertyModalOpen = ref(false);
const isContactModalOpen = shallowRef(false);
const currentPropertyModalTab = ref<Tab>('images');

const openContactModal = () => {
	isContactModalOpen.value = true;
};

const openPropertyModal = (tab: Tab) => {
	currentPropertyModalTab.value = tab;
	isPropertyModalOpen.value = true;
};

const photoModalCtaText = computed<string | undefined>(() => {
	const label = grammarInflection(numberOfImages.value, 'foto', 'fotos');

	return `${numberOfImages.value} ${label}`;
});

const shareContentRef = ref<HTMLElement | null>(null);
const shareContentButton = ref<HTMLElement | null>(null);
function toggleShareContent() {
	showShareContent.value = !showShareContent.value;
}

onClickOutside(shareContentRef, () => (showShareContent.value = false), {
	ignore: [shareContentButton]
});

function shareMobile() {
	const data = {
		title: property.value?.ad.title,
		url: shareUrl.value
	};
	if (navigator?.share && navigator?.canShare(data)) {
		navigator.share(data);
	} else {
		copy(shareUrl.value);
		toast({
			description: h(CopyToastDescription)
		});
	}
}

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		property.value?.propertyType?.name,
		property.value?.suites,
		property.value?.region,
		property.value?.city,
		property.value?.askingPrice,
		index
	);
}

function handleShareClicked(option: SharingOption) {
	trackSharePropertyClicked({
		share_whatsapp: option.label === 'WhatsApp',
		share_facebook: option.label === 'Facebook',
		copy_link: false,
		share_email: option.label === 'Via e-mail',
		share_instagram: option.label === 'Instagram',
		share_twitter: option.label === 'Twitter'
	});
}

useEventBus<void>('cta_off_market_clicked').on(() => {
	trackCtaOffMarketClicked({
		[MixpanelProps.CTA_TEXT]: 'Imóveis Off-Market',
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: 'selected properties',
		[MixpanelProps.DESTINATION_URL]: '/off-market'
	});
});

function handleBreadcrumbClicked(event: BreadcrumbItemWithIndex) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.CTA_TEXT]: event.label,
		[MixpanelProps.DESTINATION_URL]: event.route as unknown as string,
		[MixpanelProps.CLICK_HOME]: event.index === 0,
		[MixpanelProps.CLICK_CITY]: event.index === 1,
		[MixpanelProps.CLICK_NEIGHBORHOOD]: event.index === 2
	});
}

function handleViewMoreClicked(
	sectionName: SectionName,
	destination: string,
	ctaText: string
) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel',
		[MixpanelProps.DESTINATION_URL]: destination,
		[MixpanelProps.CTA_TEXT]: ctaText || 'Ver todos',
		[MixpanelProps.SECTION_NAME]: sectionName
	});
}

function handlePhotoModalOpened(
	sectionName: SectionName,
	clickType: 'photo' | 'cta',
	photoCount: number,
	photoUrl?: string,
	ctaText?: string
) {
	openPropertyModal('images');

	trackModalPhotoOpened({
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.CLICK_PHOTO]: clickType === 'photo',
		[MixpanelProps.CLICK_CTA]: clickType === 'cta',
		[MixpanelProps.PHOTO_URL]: photoUrl,
		[MixpanelProps.PHOTO_COUNT]: photoCount,
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!
	});
}

function handlePropertyVideoModalOpened(
	clickType: 'video' | 'cta',
	videoUrl?: string
) {
	openPropertyModal('videos');

	trackModalVideoOpened({
		[MixpanelProps.CLICK_VIDEO]: clickType === 'video',
		[MixpanelProps.CLICK_CTA]: clickType === 'cta',
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
		[MixpanelProps.VIDEO_URL]: videoUrl
	});
}

function handleMapModalOpened(sectionName: SectionName, ctaText: string) {
	openPropertyModal('map');

	trackMapPropertyOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!
	});
}

const { loggedIn, user } = useUserSession();

function handleFormPropertyClicked(
	sectionName: SectionName,
	ctaText: string,
	companyName: string,
	agentName: string,
	askingPrice: number | null,
	openModal: boolean = false
) {
	if (openModal) openContactModal();

	trackFormPropertyClicked({
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.COMPANY_NAME]: companyName,
		[MixpanelProps.AGENT_NAME]: agentName,
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.USER_STATUS]: loggedIn.value ? 'logged in' : 'logged out'
	});
}

usePropertyPageJsonLd(() => property.value || null);

const { heroSectionRef, propertyPricesRef, locationRef } =
	usePropertyContextualMenu();
</script>

<template>
	<div class="flex flex-col gap-8 bg-white">
		<section
			ref="heroSectionRef"
			class="relative flex h-[72vh] max-h-[500px] w-full overflow-hidden bg-primary lg-large:h-[85vh]"
		>
			<LazyPropertyModal
				v-if="property"
				v-model="isPropertyModalOpen"
				v-model:tab="currentPropertyModalTab"
				:property="property"
				:launch-year="launchYear"
				@click-photo="
					({ photoCount, photoUrl }) =>
						handlePhotoModalOpened(
							'photos modal',
							'photo',
							photoCount,
							photoUrl
						)
				"
				@click-video="
					videoUrl => handlePropertyVideoModalOpened('video', videoUrl)
				"
				@click-fixed-contact-button="
					handleFormPropertyClicked(
						'photos modal',
						heroCtaText,
						property.agent.name,
						property.company.name,
						property.askingPrice,
						true
					)
				"
			/>
			<div
				class="absolute bottom-[90px] z-20 flex flex-col items-start justify-center pl-5 pr-3 md:relative md:bottom-0 md:w-3/5 md:border-t md:border-primary-light-4 md:pl-12 lg-large:w-2/5"
			>
				<div
					v-if="isFetchingProperty"
					class="relative z-20 flex size-full flex-col justify-between gap-2 py-4 md:py-6"
				>
					<div class="w-full space-y-3">
						<Skeleton
							v-for="index in 2"
							:key="`property-hero-skeleton-line-${index}`"
							:is-active="true"
							variant="primary-light-1"
							class="h-[30px] w-11/12 last:w-6/12"
						/>

						<Skeleton
							:is-active="true"
							variant="primary-light-1"
							class="h-[30px] w-6/12"
						/>

						<Skeleton
							:is-active="true"
							variant="primary-light-1"
							class="!mt-6 h-4 w-11/12"
						/>
					</div>
					<div class="space-y-6">
						<Skeleton
							:is-active="true"
							variant="primary-light-1"
							class="h-3 w-3/12"
						/>

						<Skeleton
							:is-active="true"
							variant="primary-light-1"
							class="h-[30px] w-6/12"
						/>

						<Skeleton
							:is-active="true"
							variant="primary-light-1"
							class="h-12 w-9/12"
						/>
					</div>
				</div>

				<div
					v-else-if="property"
					class="relative flex size-full flex-col justify-between overflow-hidden"
				>
					<div
						class="flex size-full flex-col items-start gap-2 md:pr-8 md:pt-6"
					>
						<div
							v-if="property?.isExclusive"
							class="order-1 h-min max-w-64 pt-2 md:order-2 md:max-w-72"
						>
							<HighlightBadge />
						</div>
						<h1
							class="order-2 line-clamp-4 font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-white md:order-1 lg-large:text-[2rem] lg-large:font-light lg-large:tracking-[-0.04rem]"
							data-test-id="details-property-title"
						>
							{{ property?.ad.title }}
						</h1>
					</div>
					<div
						class="relative hidden min-h-fit flex-col gap-1 pb-6 pr-6 md:flex"
					>
						<div class="flex flex-col">
							<h3 class="font-sans text-sm font-normal text-white-dark-4">
								Valor do imóvel
							</h3>
							<span
								class="font-matter-sq text-[32px] font-normal tracking-[-0.64px] text-beige"
							>
								{{
									floatToLocaleString(property?.askingPrice ?? 0, {
										style: 'currency',
										minimumFractionDigits: 0,
										maximumFractionDigits: 0,
										roundingMode: 'ceil'
									})
								}}
							</span>
						</div>
						<div class="hidden w-full flex-col items-start md:flex">
							<Button
								variant="primary-dark"
								class="w-fit px-12 py-3"
								data-test-id="details-contact-button"
								@click="
									handleFormPropertyClicked(
										'hero section',
										heroCtaText,
										property.agent.name,
										property.company.name,
										property.askingPrice,
										true
									)
								"
							>
								{{ heroCtaText }}
							</Button>
							<ContactFormModal
								v-model="isContactModalOpen"
								context="property"
								:broker="property.agent"
								:company-name="property.company.name"
								:property-commercial-id="property.commercialId"
								:property-region="property.region"
								:property-type-name="property.propertyType?.name"
								:property-id="property.id"
								:asking-price="property.askingPrice"
							/>
						</div>
					</div>
				</div>
			</div>

			<div
				class="relative flex size-full flex-wrap overflow-hidden bg-white md:w-4/5"
			>
				<div
					class="grid size-full grid-cols-1 gap-1 overflow-hidden lg-large:grid-cols-2"
					:class="{
						'!grid-cols-1': property?.images?.length === 1
					}"
				>
					<div class="relative col-span-1 max-h-[500px] w-full">
						<div
							class="absolute bottom-0 z-20 flex w-full items-center justify-start gap-4 px-[20px] pb-6 lg-large:w-1/2 2xl:w-2/5"
						>
							<HeroButton
								class="max-w-32 text-sm font-semibold"
								@click="
									handlePhotoModalOpened(
										'hero',
										'cta',
										3,
										undefined,
										photoModalCtaText
									)
								"
							>
								<template #icon>
									<IconMaterialSymbolsLightImageOutline
										class="size-5 text-beige"
									/>
								</template>

								{{ photoModalCtaText }}
							</HeroButton>

							<HeroButton
								v-if="numberOfVideos"
								class="max-w-32 text-sm font-semibold"
								@click="handlePropertyVideoModalOpened('cta')"
							>
								<template #icon>
									<IconMaterialSymbolsPlayArrowOutline
										class="size-5 text-beige"
									/>
								</template>

								{{ numberOfVideos }}

								{{ grammarInflection(numberOfVideos, 'vídeo', 'vídeos') }}
							</HeroButton>

							<HeroButton
								class="max-w-32 text-sm font-semibold"
								@click="handleMapModalOpened('hero section', 'Mapa')"
							>
								<template #icon>
									<IconMaterialSymbolsLightMapOutline
										class="size-5 text-beige"
									/>
								</template>
								Mapa
							</HeroButton>
						</div>
						<span
							class="absolute inset-0 size-full cursor-pointer bg-[linear-gradient(0deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0.2)_100%),linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_100%)] md:hidden"
							@click="
								handlePhotoModalOpened(
									'hero section',
									'photo',
									3,
									property?.images[0]?.watermarkUrl
								)
							"
						>
						</span>

						<Skeleton
							:is-active="isFetchingProperty"
							class="size-full"
							variant="white"
						>
							<NuxtImg
								:src="firstImage || ''"
								:alt="getImageAlt(0)"
								class="size-full cursor-pointer object-cover"
								provider="AWSServerlessImageHandler"
								:placeholder="[50, 25]"
								format="webp"
								quality="80"
								preload
								loading="eager"
								@click="
									handlePhotoModalOpened(
										'hero section',
										'photo',
										3,
										property?.images[0]?.watermarkUrl
									)
								"
							/>
						</Skeleton>
					</div>

					<div
						v-if="hasSecondAndThirdImages"
						class="relative hidden h-full grid-cols-[repeat(1,_1fr)] grid-rows-[repeat(2,_minmax(248px,_1fr))] gap-1 overflow-hidden sm:grid"
					>
						<div v-for="index in 2" :key="`hero-image-${index}`">
							<Skeleton
								:is-active="isFetchingProperty"
								class="size-full"
								variant="white"
							>
								<NuxtImg
									:src="property?.images[index]?.watermarkUrl"
									:alt="getImageAlt(index)"
									class="size-full cursor-pointer object-cover"
									provider="AWSServerlessImageHandler"
									:placeholder="[50, 25]"
									format="webp"
									quality="80"
									preload
									loading="eager"
									@click="
										handlePhotoModalOpened(
											'hero section',
											'photo',
											3,
											property?.images[index]?.watermarkUrl
										)
									"
								/>
							</Skeleton>
						</div>
					</div>
				</div>

				<div
					class="absolute right-10 top-10 z-20 size-5 cursor-pointer md:right-20 md:top-5"
				>
					<div class="flex flex-col">
						<button
							ref="shareContentButton"
							class="z-10 hidden size-11 w-fit items-center justify-center border border-white-dark-3 bg-white px-[14px] py-2 md:flex"
							@click="toggleShareContent"
						>
							<IconMaterialSymbolsShare
								class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
							/>
						</button>
						<button
							class="z-10 flex size-11 w-fit items-center justify-center bg-white px-[14px] py-2 md:hidden"
							@click="shareMobile"
						>
							<IconMaterialSymbolsShare
								class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
							/>
						</button>
					</div>

					<div
						v-show="showShareContent"
						ref="shareContentRef"
						class="absolute right-[-30px] top-[-42px] w-[125px] translate-y-[60%] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
					>
						<ShareContent
							v-model="showShareContent"
							variant="dropdown"
							mail-subject="Leia mais sobre este imóvel"
							:content-to-share="shareUrl"
							show-labels
							@share-clicked="handleShareClicked"
						/>
					</div>
				</div>
			</div>
		</section>

		<section class="w-full divide-y divide-white-dark-3">
			<div class="w-full">
				<FixedContactButton
					v-if="property"
					:first-name="agentFirstName"
					:display-after="propertyPricesRef"
					:hide-after="locationRef"
					:hide-offset="350"
					@click="
						handleFormPropertyClicked(
							'mobile floating cta',
							heroCtaText,
							property.company.name,
							property.agent.name,
							property.askingPrice,
							true
						)
					"
				/>
				<Container
					class="flex flex-col divide-y divide-white-dark-3 !p-0 md:!px-8 lg-large:!px-12"
				>
					<div
						class="flex size-full flex-col-reverse lg-large:flex-row lg-large:gap-8"
					>
						<div class="flex flex-col gap-10 lg-large:w-3/5 2xl:w-full">
							<div
								id="overview"
								ref="overviewRef"
								data-section-name="Visão geral"
								class="hidden w-fit items-center justify-start px-5 md:justify-start md:px-0 lg-large:flex"
							>
								<Breadcrumb
									:items="breadcrumbItems"
									:is-loading="isFetchingProperty"
									@breadcrumb-clicked="handleBreadcrumbClicked"
								/>
								<CopyButton @click-copy="copyCommercialId" />
							</div>

							<div
								class="flex w-full flex-col items-center justify-center divide-y divide-white-dark-3 text-primary sm:items-start sm:justify-start"
							>
								<div
									class="flex w-full flex-col items-center gap-10 px-5 pb-10 md:px-0 md:pb-12"
								>
									<CharacteristicsGrid
										:is-loading="isFetchingProperty"
										:characteristics="characteristics"
									/>

									<PropertyDescriptionSection
										:summary="property?.propertySummary"
										:is-loading="isFetchingProperty"
										:ad="property?.ad"
									/>
								</div>

								<div
									v-if="shouldShowCharacteristicsAndHighlightedImagesSection"
									id="characteristics"
									ref="characteristicsRef"
									data-section-name="Características"
									class="flex w-full flex-col gap-12 px-5 py-10 md:px-0 md:py-12"
								>
									<FeaturesGrid
										v-if="shouldShowFeaturesSection"
										:is-loading="isFetchingProperty"
										:features-by-category="property?.featuresByCategory || {}"
									/>

									<PropertyHighlightedImagesSection
										v-if="shouldShowHighlightedImagesSection"
										:images="property?.images || []"
										:is-loading="isFetchingProperty"
										:property-type="property?.propertyType?.name"
										:suites="property?.suites"
										:region="property?.region"
										:city="property?.city"
										:asking-price="property?.askingPrice"
										@open-modal="
											({ clickType, photoUrl, ctaText }) =>
												handlePhotoModalOpened(
													'property atributes',
													clickType,
													5,
													photoUrl,
													ctaText
												)
										"
									/>
								</div>

								<div
									id="location"
									ref="locationRef"
									data-section-name="Localização"
									class="w-full px-5 md:px-0"
								>
									<PropertyLocationSection
										:region="property?.region || ''"
										:city="property?.city || ''"
										:state="property?.state || ''"
										:pluscode-group="property?.pluscodeGroup || ''"
										:is-loading="isFetchingProperty"
										@open-map-modal="
											({ ctaText }) =>
												handleMapModalOpened('property location', ctaText)
										"
									/>
								</div>

								<div
									id="contact"
									ref="contactRef"
									data-section-name="Atendimento"
									class="flex w-full flex-col gap-6 px-5 py-10 md:px-0 md:py-12"
								>
									<PropertyContactSection
										:is-loading="isFetchingProperty"
										:agent="property?.agent || {} as Agent"
										:company-logo="property?.company.smallLogo || ''"
										:company-name="property?.company.name || ''"
										:commercial-id="property?.commercialId || ''"
										:region="property?.region || ''"
										:property-type="property?.propertyType || {} as PropertyTypeObject"
										:property-id="property?.id || ''"
										:asking-price="property?.askingPrice || 0"
										class="w-full"
										@form_property_clicked="
											({ ctaText, companyName, agentName, askingPrice }) =>
												handleFormPropertyClicked(
													'service',
													ctaText,
													companyName,
													agentName,
													askingPrice
												)
										"
									/>
								</div>

								<HighlightedRegionPropertiesDetailsSection
									v-if="
										similarProperties?.length || isFetchingSimilarProperties
									"
									ref="highlightedRegionPropertiesDetailsSectionRef"
									:properties="similarProperties || []"
									:region="property?.region || ''"
									:title="`Imóveis semelhantes em ${property?.region}`"
									button-text="Ver todos os imóveis semelhantes"
									:button-href="similarPropertiesHref"
									:is-loading="isFetchingSimilarProperties"
									container-class="pt-10 xl:pt-12 !px-0"
									button-variant="secondary"
									title-class="px-5 md:!px-0"
									button-class="border-none hover:underline underline-offset-4 decoration-white-dark-3 px-5 md:!px-0"
									@cta-view-more-clicked="
										({ destination, ctaText }) =>
											handleViewMoreClicked(
												'similar properties',
												destination,
												ctaText
											)
									"
								/>
							</div>
						</div>

						<aside
							class="relative top-0 w-full overflow-y-visible pb-6 sm:min-w-[400px] lg-large:w-2/5 lg-large:min-w-[200px] lg-large:pb-[60px] xl:min-w-[300px]"
						>
							<div
								id="mobileOverview"
								ref="mobileOverviewRef"
								data-section-name="Visão geral"
								class="flex h-auto w-fit items-center justify-start px-5 pb-4 md:justify-start md:px-0 lg-large:hidden"
							>
								<Breadcrumb
									:is-loading="isFetchingProperty"
									:items="breadcrumbItems"
									@breadcrumb-clicked="handleBreadcrumbClicked"
								/>
								<CopyButton @click-copy="copyCommercialId" />
							</div>
							<div
								ref="propertyPricesRef"
								class="sticky top-[103px] z-10 px-5 md:px-0"
								:class="{ 'top-[181px]': navigationStore.isHeaderVisible }"
							>
								<PropertyPrices
									v-model:open="isContactModalOpen"
									:is-loading="isFetchingProperty"
									:asking-price="property?.askingPrice"
									:condo-fee="property?.condoFee"
									:condo-name="property?.condoName"
									:tax="property?.tax"
									:agent="property?.agent"
									:company-name="property?.company.name"
									:launch-year="launchYear"
									:property-type-name="property?.propertyType?.name"
									:property-type="property?.propertyType"
									:property-id="property?.id"
									:commercial-id="property?.commercialId"
									:company-logo="property?.company.smallLogo"
									:region="property?.region"
									price-classes="text-primary"
									section-name="card (property details)"
								/>
							</div>
						</aside>
					</div>
				</Container>
			</div>

			<div id="chosenForYou" ref="chosenForYouRef" class="w-full">
				<Container
					v-if="
						(chosenForYouProperties && chosenForYouProperties.length >= 5) ||
						isFetchingChosenForYouProperties
					"
					class="size-full bg-white !pt-0 pb-11 lg-large:pb-[60px]"
				>
					<ChosenPropertiesSection
						:properties="chosenForYouProperties || []"
						:is-loading="isFetchingChosenForYouProperties"
						:cta-link="chosenForYouCtaLink"
						class="w-full !p-0 md:!py-0"
						container-class="!justify-start !flex !items-start !mx-0 !px-0 !pb-0"
						main-container-class="!w-full !mx-0 flex flex-col justify-start items-start py-0"
						@cta_view_more_clicked="
							({ href, ctaText }) =>
								handleViewMoreClicked('selected properties', href, ctaText)
						"
					/>
				</Container>
			</div>
		</section>
	</div>
</template>
